/**
 * Provides a way of requesting user data from the server and receiving responses.
 * Currently, the plan is to save all user data to the cache, and re-request all data
 * that is older than X hours (from the cache). Getting a user or list of users should be
 * very fast, since we likely won't have to re-download their avatar image. Avatar images
 * should also be aggressively cached in much the same way as files.
 */

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { apiIdFor, EntityApiId } from "~/classes/entity"
import { sendUserFindRequestBatch } from "~/classes/handlers/user/find"
import { InterfaceUser } from "~/classes/users/InterfaceUser"

class SavedUsers extends GlobalComposable<{
  publicUserInfo: Record<string, InterfaceUser>
}> {
  override defaultState = () => ({
    publicUserInfo: {},
  })

  override onCreate = () => {
    const { addInterfaceEventListener } = useEvents()
    addInterfaceEventListener("userFindResponse", this.onUserFindResponse)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("userFindResponse", this.onUserFindResponse)
  }

  userType = (userId: string) => {
    return apiIdFor(userId)
  }

  isHuman = (userId: string) => {
    return this.userType(userId) === EntityApiId.UserIdV0
  }

  /** Fetches the user info if not cached and returns a reactive ref to it */
  computedPublicUserInfo = (userId: string): Ref<InterfaceUser | undefined> => {
    if (!this.isHuman(userId))
      return ref(undefined)
    this.fetchUserInfoIfNotCached([userId])
    return toRef(this.state.value.publicUserInfo, userId)
  }

  findUserIdByEmail = async (email: string, timeoutMs = 5000) => {
    const { waitForFirstResponse } = useEvents()
    const requestId = generateWorkerRequestId()

    sendUserFindRequestBatch([{
      requestId,
      email,
      offline: false,
    }])

    const response = await withTimeout(
      waitForFirstResponse("userFindResponse", requestId),
      timeoutMs,
    )
    if (response.error || !isDefined(response.user))
      throw new Error("User not found")

    return response.user.id
  }

  /**
   * Gets user info from the cache and then from the server. The cache wil re-request
   * data that is older than a certain level, automatically.
   *
   * @returns the request id
   */
  sendUserInfoRequest = (
    userId?: string,
    email?: string,
    handle?: EntityHandle,
  ) => {
    const { state: myself } = useSelf()

    // check if this is just ourselves
    if (myself.value.user) {
      if ((userId && myself.value.user?.id === userId)
        || (email && myself.value.user?.email() === email)
        || (handle && myself.value.user?.handle() === handle)
      ) {
        this.state.value.publicUserInfo[myself.value.user.id] = myself.value.user
        return
      }
    }

    const request: UserFindRequestDatagram = {
      requestId: generateWorkerRequestId(),
      userId,
      email,
      handle,
      offline: useCache().isOffline(),
    }
    sendUserFindRequestBatch([request])
    return request
  }

  fetchUserInfoIfNotCached = (userIds: string[]) => {
    const { state: myself } = useSelf()

    const requests: UserFindRequestDatagram[] = []
    for (const userId of userIds) {
      if (isDefined(this.state.value.publicUserInfo[userId]))
        continue
      else if (myself.value.user && myself.value.user?.id === userId) {
        this.state.value.publicUserInfo[myself.value.user.id] = myself.value.user
        continue
      }
      else {
        requests.push({
          requestId: generateWorkerRequestId(),
          userId,
          offline: useCache().isOffline(),
        })
      }
    }

    if (requests.length === 0)
      return

    sendUserFindRequestBatch(requests)
  }

  onUserFindResponse = (response: UserFindResponseDatagram) => {
    const { user, error } = response
    if (error || !user)
      return
    this.state.value.publicUserInfo[user.id] = new InterfaceUser(user)
  }
}

const savedUsers = new SavedUsers("saved-users", theme.colors.purple.hex)
export default function () {
  return savedUsers.use()
}
